// @flow
import React from 'react';
import { Redirect, Route, Switch } from 'react-router';
import { useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import { isEmpty, omit } from 'lodash';

import { type Callbacks } from 'types/api';
import { type User } from './types';

import { errorResponseFormatter } from 'utils/apiHelpers';
import PasswordReqs from 'apps/core/auth/PasswordReqs';
import { isRequired } from 'utils/formHelpers';
import { Alert, Card, Col, Row } from 'bs';
import { Input, Select } from 'components/FormikFields';
import SubmitButton from 'components/SubmitButton';
import Tabs from 'components/Tabs';
import WithRouteProps from 'components/WithRouteProps';

import MFASettings from './MFASettings';

type Props = {
    user: User,
    saveUser: (userData: Object, callbacks: Callbacks) => void,
    fetchUser: (callbacks: Callbacks) => void,
    addNotification: (msg: string) => void,
};

const APP_PATH = '/profile';

const submitSaveUser = (values, callbacks, saveUser, onSuccess) => {
    saveUser(omit(values, 'password_rpt'), callbacks);
};

const General = ({ user, saveUser, addNotification }: Props) => {
    const onSuccess = () => addNotification('Saved!');

    return (
        <Formik
            onSubmit={(values, actions) => {
                const callbacks = {
                    start: () => {
                        actions.setSubmitting(true);
                        actions.setStatus(null);
                    },
                    done: () => {
                        actions.setSubmitting(false);
                        onSuccess();
                    },
                    fail: apiError => {
                        actions.setSubmitting(false);
                        actions.setErrors(errorResponseFormatter(apiError.json));
                        actions.setStatus('Correct errors below.');
                    },
                };
                submitSaveUser(values, callbacks, saveUser, onSuccess);
            }}
            initialValues={user}
        >
            {({ touched, isValid, status, isSubmitting }) => {
                return (
                    <Form>
                        {status && <Alert flavor='danger'>{status}</Alert>}
                        <Input
                            name='first_name'
                            label='First Name'
                            validate={isRequired}
                        />
                        <Input name='last_name' label='Surname' validate={isRequired} />
                        <Select
                            label='Email Notification Preferences'
                            name='notification_email_preference'
                            required={true}
                            validate={isRequired}
                        >
                            <option value='immediately'>
                                Immediately as events happen.
                            </option>
                            <option value='daily'>
                                Email me a daily summary of events.
                            </option>
                            <option value='never'>
                                Do not send email notifications.
                            </option>
                        </Select>

                        <SubmitButton
                            disabled={!isValid || isEmpty(touched)}
                            submitting={isSubmitting}
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};

const Password = ({ user, saveUser, addNotification }: Props) => {
    const onSuccess = () => addNotification('Saved!');

    return (
        <Formik
            initialErrors={{ current_password: '' }}
            initialValues={{
                password: '',
                current_password: '',
                password_rpt: '',
            }}
            validate={values => {
                const errors = {};
                if (values.password !== values.password_rpt) {
                    errors.password_rpt = 'Passwords do not match.';
                }
                return errors;
            }}
            onSubmit={(values, actions) => {
                const callbacks = {
                    start: () => {
                        actions.setSubmitting(true);
                        actions.setStatus(null);
                    },
                    done: () => {
                        actions.setSubmitting(false);
                        actions.resetForm();
                        onSuccess();
                    },
                    fail: apiError => {
                        actions.setSubmitting(false);
                        actions.setErrors(errorResponseFormatter(apiError.json));
                        actions.setStatus('Correct errors below.');
                    },
                };
                submitSaveUser(values, callbacks, saveUser, onSuccess);
            }}
        >
            {({ errors, isValid, status, isSubmitting }) => (
                <Form>
                    <Input
                        name='current_password'
                        type='password'
                        label='Enter Current Password'
                        autoComplete='current-password'
                        validate={isRequired}
                    />
                    <Input
                        name='password'
                        type='password'
                        label='New Password'
                        autoComplete='new-password'
                        validate={isRequired}
                        helpText={
                            <PasswordReqs
                                className={
                                    status && errors['password'] ? 'text-danger' : ''
                                }
                            />
                        }
                    />
                    <Input
                        type='password'
                        name='password_rpt'
                        label='Repeat Password'
                        autoComplete='new-password'
                        validate={isRequired}
                    />
                    <SubmitButton disabled={!isValid} submitting={isSubmitting} />
                </Form>
            )}
        </Formik>
    );
};

const TwoFactor = ({ user, fetchUser, addNotification }: Props) => {
    return (
        <div>
            <MFASettings user={user} fetchUser={fetchUser} />
        </div>
    );
};

const tabs = [
    {
        key: 'general',
        title: 'General',
        to: `${APP_PATH}/general`,
        render: props => <General {...props} />,
    },
    {
        key: 'password',
        title: 'Password',
        to: `${APP_PATH}/password`,
        render: props => <Password {...props} />,
    },
    {
        key: 'two-factor',
        title: 'Two-Factor Authentication',
        to: `${APP_PATH}/two-factor`,
        render: props => <TwoFactor {...props} />,
    },
];

const UserProfile = (props: Props) => (
    <Row gutters={false} className='justify-content-center'>
        <Col className='mt-5' xs={10} sm={8} lg={6}>
            <Card header='Profile'>
                <Switch>
                    <Route path={APP_PATH} exact={true}>
                        <Redirect to={`${APP_PATH}/general`} />
                    </Route>
                    <Route path={`${APP_PATH}/:section`}>
                        <WithRouteProps render={({ params }) => (
                            <Tabs
                                tabs={tabs}
                                activeTab={params.section}
                                extraProps={props}
                            />
                        )}/>
                    </Route>
                </Switch>
            </Card>
        </Col>
    </Row>
);

export default UserProfile;
